/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { Box, FormControlLabel, FormGroup, Grid, Switch } from '@mui/material'
import { JourneyType } from 'app/constants/Booking/BookingConstants'
import { BookingSummary } from 'components/Bookings/BookingSummary/BookingSummary'
import JourneyPlan from 'components/Bookings/QuickQuote/JourneyPlan'
import SectionHeader from 'components/Common/SectionHeader'
import AutoComplete from 'components/RouteWizard/AutoComplete'
import ConnectForm from 'components/RouteWizard/ConnectForm'
import Form from 'components/RouteWizard/Form'
import { StepComponentProps } from 'components/RouteWizard/RouteWizard'
import { Select } from 'components/RouteWizard/Select'
import useClient from 'hooks/useClient'
import useClientUsers from 'hooks/useClientUsers'
import useClients from 'hooks/useClients'
import useFormState from 'hooks/useFormState'
import useUserRole from 'hooks/useUserRole'
import { DateTime } from 'luxon'
import React, { useEffect, useRef, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'
import AccountReference from '../QuickQuote/AccountReference'
import AccountSelect from './AccountSelect'

import qqSchema from './quickquote-schema'
import schema from './schema'

const formatClientAccountLabel = (client?: any) => {
  const companyName = client?.companyName?.trim?.() || ''
  const alias = client?.alias?.trim?.() || ''

  if (companyName && alias) {
    return `${companyName} (Ref: ${alias})`
  }

  return companyName || alias || ''
}

const AccountInformation = ({
  initialValues,
  onSuccess,
}: StepComponentProps) => {
  const formMethods = useRef<UseFormReturn | null>(null)

  const currentClient = useSelector((state: any) => state.clients.client)

  const { clients, options: clientOptions } = useClients()
  const [onlyTraveller] = useState<boolean>(
    useUserRole().currentUserRoles.length === 1 &&
      useUserRole().currentUserRoles[0] === 'TRAVELLER',
  )

  const [clientType, setClientType] = useState<'my' | 'other'>(
    initialValues?.accountType
      ? initialValues?.accountType
      : onlyTraveller
      ? 'my'
      : clients?.length > 1
      ? 'other'
      : 'my',
  )

  const [onBehalfOfToggle, setOnBehalfOfToggle] = useState<boolean>(
    initialValues?.onBehalfOf ? true : false,
  )

  const [numPassengers, setNumPassengers] = useState(
    initialValues?.numberOfPassengers || 1,
  )
  const selectedClientId = useFormState('clientId', formMethods.current)

  const clientId =
    clientType === 'my'
      ? currentClient?.id
      : typeof selectedClientId !== 'number'
      ? parseInt(selectedClientId)
      : selectedClientId
  const mainPassenger = useFormState('mainPassenger', formMethods.current)
  const quickQuote = useSelector((state: any) => state.bookings.quickQuote)

  const { users } = useClientUsers(clientId)

  const currentUser = useSelector((state: any) => state.users.user)
  const [showToggle, setShowToggle] = useState<boolean>(false)

  const { fixedPriceDisplay, displayVATPrice } = useClient(selectedClientId)
  const [preview, setPreview] = useState({})
  const formattedClientOptions = React.useMemo(
    () =>
      Object.fromEntries(
        (clients || [])
          .sort((a: any, b: any) => (a.companyName > b.companyName ? 1 : -1))
          .map((client: any) => [
            client.id,
            formatClientAccountLabel(client).toUpperCase(),
          ]),
      ),
    [clients],
  )

  useEffect(() => {
    setPreview({
      passengers: [mainPassenger],
      companyName: formatClientAccountLabel(
        clients?.find((c: any) => c.id === parseInt(clientId, 10)),
      ),
      fixedPrice: fixedPriceDisplay,
      displayVATPrice: displayVATPrice,
    })
  }, [mainPassenger, clients, fixedPriceDisplay, clientId])

  const checkDateAndTime = (data?: any) => {
    //if immediate journey - all good
    if (data?.isImmediateJourney) {
      return true
    }
    //if not immediate journey then it needs to be +30 minutes from now
    const outboundWhenTimeZone = DateTime.fromJSDate(
      data.outbound.when,
    ).setZone(data.outbound.timeZone, { keepLocalTime: true })

    const timeZoneNow = DateTime.now().setZone(data.outbound.timeZone)
    const diff = outboundWhenTimeZone.diff(timeZoneNow, ['minutes']).toObject()

    if ((diff.minutes || 0) < 30) {
      formMethods.current?.setError('outbound.when', {
        type: 'custom',
        message: `Time must be after ${timeZoneNow
          .plus({
            minutes: 30,
          })
          .toLocaleString(
            DateTime.DATETIME_MED,
          )}, or select One Way and ASAP for an immediate pick up`,
      })
      return false
    }

    const inboundWhenTimeZone = DateTime.fromJSDate(data.inbound.when).setZone(
      data.outbound.timeZone,
      { keepLocalTime: true },
    )
    const outboundTimeZone = DateTime.fromJSDate(data.outbound.when).setZone(
      data.outbound.timeZone,
      { keepLocalTime: true },
    )
    const diffInbound = inboundWhenTimeZone
      .diff(outboundTimeZone, ['minutes'])
      .toObject()

    const journeyTypeName = formMethods.current?.getValues('journeyTypeName')

    if (
      (diffInbound?.minutes || 0) < 30 &&
      journeyTypeName === JourneyType.RETURN
    ) {
      formMethods.current?.setError('inbound.when', {
        type: 'custom',
        message: `Please select a time after pickup`,
      })
      return false
    }
    return true
  }

  const onSubmit = (data: any) => {
    if (quickQuote) {
      if (!checkDateAndTime(data)) {
        return false
      }
    }
    const journeyTypeName = formMethods.current?.getValues('journeyTypeName')
    const inbound = formMethods.current?.getValues('inbound')
    const outbound = formMethods.current?.getValues('outbound')
    const requiresDropoff = journeyTypeName !== JourneyType.AS_DIRECTED
    const hasPickup = !!outbound?.pickup
    const hasDropoff = !!outbound?.dropOff

    if (quickQuote && (!hasPickup || (requiresDropoff && !hasDropoff))) {
      if (!hasPickup) {
        formMethods.current?.setError('outbound.pickup', {
          type: 'manual',
          message: `Please select a pickup location`,
        })
      }
      if (requiresDropoff && !hasDropoff) {
        formMethods.current?.setError('outbound.dropOff', {
          type: 'manual',
          message: `Please select a dropOff location`,
        })
      }
      return false
    }

    const includePlan = quickQuote
      ? {
          plan: {
            additionalStops: formMethods.current?.getValues('additionalStops'),
            inbound:
              journeyTypeName == JourneyType.RETURN
                ? {
                    ...inbound,
                    pickup: outbound?.dropOff,
                    dropOff: outbound?.pickup,
                  }
                : null,
            isImmediateJourney:
              formMethods.current?.getValues('isImmediateJourney'),
            journeyTypeName: formMethods.current?.getValues('journeyTypeName'),
            outbound: outbound,
          },
        }
      : {}

    const passengers = quickQuote
      ? [
          {
            isLeadPassenger: true,
            name: `${currentUser.firstName} ${currentUser.lastName}`,
            userId: currentUser.userId,
            contactNumber: currentUser.contact.mobileNumber,
            email: currentUser.email,
            requiresContactNumber: false,
            requiresEmail: false,
          },
        ]
      : [mainPassenger, ...(data?.passengers || [])]

    onSuccess({
      account: {
        ...data,
        accountType: clientType,
        clientId: clientType === 'my' ? currentClient.id : data.clientId,
        numberOfPassengers: numPassengers,
        fixedPrice: fixedPriceDisplay,
      },
      preview: {
        ...preview,
        passengers,
      },
      ...includePlan,
    })
  }

  const callbackPreview = (preview: object) => {
    setPreview((prev) => ({ ...prev, ...preview }))
  }

  useEffect(() => {
    if (users && users?.length === 0) {
      setShowToggle(false)
    } else {
      setShowToggle(true)
    }
    formMethods.current?.resetField('onBehalfOf')
  }, [users])

  const onBehalfOfToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOnBehalfOfToggle(event.target.checked)
    if (!event.target.checked) {
      formMethods.current?.resetField('onBehalfOf', { defaultValue: '' })
    }
  }

  useEffect(() => {
    //Requirements
    if (formMethods.current?.formState.isDirty) {
      formMethods.current?.resetField('onBehalfOf', { defaultValue: '' })
      formMethods.current?.resetField('bookingReferenceValues', {
        defaultValue: [],
      })
      formMethods.current?.resetField('passengers', {
        defaultValue: [],
      })
      formMethods.current?.resetField('mainPassenger', {
        defaultValue: {
          isLeadPassenger: true,
          name: '',
          userId: undefined,
          requiresContactNumber: false,
          requiresEmail: false,
          contactNumber: '',
          email: '',
        },
      })
      setOnBehalfOfToggle(false)
      setNumPassengers(1)
    }
  }, [formMethods.current?.watch('clientId'), quickQuote])

  useEffect(() => {
    if (!initialValues?.accountType && !onlyTraveller) {
      clients?.length > 1 ? setClientType('other') : setClientType('my')
    }
  }, [clients])

  useEffect(() => {
    if (clientType === 'my') {
      formMethods.current?.setValue('clientId', currentClient?.id)
      formMethods.current?.resetField('onBehalfOf', { defaultValue: '' })
      setOnBehalfOfToggle(false)
    }
  }, [clientType])

  useEffect(() => {
    if (quickQuote) {
      formMethods.current?.resetField('passengers', {
        defaultValue: [],
      })
      formMethods.current?.setValue('mainPassenger', {
        isLeadPassenger: true,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        userId: currentUser.userId,
        contactNumber: currentUser.contact.mobileNumber,
        email: currentUser.email,
        requiresContactNumber: false,
        requiresEmail: false,
      })
    }
  }, [quickQuote])

  return (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <Form
          schema={quickQuote ? qqSchema : schema}
          ref={formMethods}
          onSubmit={onSubmit}
          defaultValues={{
            ...initialValues,
            clientId: initialValues?.clientId || '0',
          }}
          showControls={false}
          formId={'accountInformation'}
          sx={{ float: 'right', marginRight: '40px' }}
          syncLeaveWarningWithDirty
        >
          {!onlyTraveller && (
            <SectionHeader
              title="select which account"
              subtitle="Please select which account the booking is from."
            />
          )}
          <ConnectForm>
            {!onlyTraveller && (
              <Box
                component="div"
                sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
              >
                <AccountSelect
                  icon={PersonOutlineIcon}
                  label="My Account"
                  active={clientType === 'my'}
                  onClick={() => setClientType('my')}
                />
                <AccountSelect
                  icon={PeopleOutlineIcon}
                  label="Client Account"
                  active={clientType === 'other'}
                  onClick={() => setClientType('other')}
                />
              </Box>
            )}
            {clientType === 'other' && (
              <>
                <Select
                  options={formattedClientOptions}
                  name="clientId"
                  label="Select Client"
                  topRow={{ value: '0', label: 'Please Select Client' }}
                  searchable
                />
                {showToggle && (
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={onBehalfOfToggle}
                          onChange={onBehalfOfToggleChange}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                      label="Book on behalf of a specific client user?"
                    />
                  </FormGroup>
                )}
                {onBehalfOfToggle && showToggle && (
                  <AutoComplete
                    name="onBehalfOf"
                    options={
                      users?.sort((a: any, b: any) =>
                        a.firstName > b.firstName ? 1 : -1,
                      ) || []
                    }
                    label="On behalf of"
                    disableClearable={true}
                    getOptionLabel={(user) => {
                      if (user == null) {
                        return ''
                      }
                      if (typeof user === 'string') {
                        return user
                      }
                      const name =
                        [user.firstName, user.lastName]
                          .filter(Boolean)
                          .join(' ') || 'awaiting details'
                      return [name, user.email && `(${user.email})`]
                        .filter((n) => !!n)
                        .join(' ')
                    }}
                  />
                )}
              </>
            )}
          </ConnectForm>
          {!quickQuote ? (
            <AccountReference
              onlyTraveller={onlyTraveller}
              clientId={clientId}
              selectedClientId={selectedClientId}
              numPassengers={numPassengers}
              setNumPassengers={setNumPassengers}
            />
          ) : (
            <>
              <Box
                component="hr"
                sx={{
                  margin: '20px 0',
                  borderColor: '#828282',
                  opacity: '0.2',
                }}
              />
              <JourneyPlan
                onSuccess={() => ({})}
                initialValues={initialValues}
                formMethods={formMethods}
                callbackPreview={callbackPreview}
              />
            </>
          )}
        </Form>
      </Grid>
      <Grid item xs={5}>
        <BookingSummary
          bookingPreview={preview}
          formId={'accountInformation'}
          s
        />
      </Grid>
    </Grid>
  )
}

export default AccountInformation
